import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

export default function UniversePeriodModal({ onSubmit, onClose, selectedTable, tableHint }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [error, setError]         = useState(null);

  const handleSubmit = () => {
    if (!startDate || !endDate) {
      setError('Both start and end dates are required.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date.');
      return;
    }
    onSubmit({ startDate, endDate });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="text-center">
            <h2 className="text-lg font-bold text-slate-800 mb-1">
              Enter Universe Request Period
            </h2>
            <p className="text-slate-500 text-sm">
              Required for: <strong className="text-slate-700">{selectedTable?.label}</strong>
              <span className="inline-block ml-2 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-semibold rounded-full border border-slate-200">
                {selectedTable?.short}
              </span>
            </p>
            {tableHint && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-rose-50/80 to-orange-100/60 rounded-xl border border-rose-200/50">
                <span
                  data-tooltip-id="universe-hint"
                  data-tooltip-content={tableHint}
                  className="cursor-pointer text-rose-600 hover:text-rose-700 text-lg"
                >
                  💡
                </span>
                <span className="text-xs text-rose-700">Hover for details</span>
                <Tooltip id="universe-hint" place="top" effect="solid" className="max-w-xs" />
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Body */}
        <div className="px-8 py-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <span className="inline-flex items-center gap-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-rose-500 to-orange-600 rounded-full"></div>
                  Start Date
                </span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="
                  w-full rounded-xl border border-slate-300/60 px-4 py-3 text-base
                  focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400
                  bg-white/80 backdrop-blur-sm transition-all duration-200
                  hover:border-slate-400/60 hover:bg-white/90
                "
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <span className="inline-flex items-center gap-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-amber-600 rounded-full"></div>
                  End Date
                </span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="
                  w-full rounded-xl border border-slate-300/60 px-4 py-3 text-base
                  focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400
                  bg-white/80 backdrop-blur-sm transition-all duration-200
                  hover:border-slate-400/60 hover:bg-white/90
                "
              />
            </div>
          </div>

          {error && (
            <div className="p-4 bg-gradient-to-r from-red-50/80 to-pink-100/60 rounded-xl border border-red-200/50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-pink-600 rounded-full"></div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Enhanced Footer */}
          <div className="flex items-center justify-between pt-4">
            <button
              onClick={onClose}
              className="
                px-6 py-3 text-base font-semibold text-slate-600 hover:text-slate-900
                rounded-xl border border-slate-300/60 hover:border-slate-400/60
                bg-white/80 hover:bg-slate-50/80 transition-all duration-200
                hover:shadow-md hover:-translate-y-0.5
              "
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="
                inline-flex items-center justify-center rounded-xl px-6 py-3
                text-base font-bold text-white
                bg-gradient-to-r from-rose-500 to-orange-600
                hover:from-rose-600 hover:to-orange-700
                shadow-lg shadow-rose-500/25 hover:shadow-xl hover:shadow-rose-500/30
                transition-all duration-200 hover:-translate-y-0.5 hover:scale-105
                focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:ring-offset-2
              "
            >
              <span>Confirm Period</span>
              <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
